import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiImage, FiSearch, FiFilter } from 'react-icons/fi';
import axiosInstance from '../utils/api';

const VendorAddons = () => {
    const [addons, setAddons] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingAddon, setEditingAddon] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'other',
        price: '',
        image: '',
        eligibleForSubsidy: true,
        vendorId: ''
    });

    // Image upload states
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);

    const categories = [
        { value: 'nail_care', label: 'Nail Care' },
        { value: 'hair_care', label: 'Hair Care' },
        { value: 'skin_care', label: 'Skin Care' },
        { value: 'touch_up', label: 'Touch Up' },
        { value: 'accessories', label: 'Accessories' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        fetchVendors();
    }, []);

    useEffect(() => {
        if (selectedVendor) {
            fetchAddons(selectedVendor);
        }
    }, [selectedVendor]);

    const fetchVendors = async () => {
        try {
            const response = await axiosInstance.get('/admin/vendor/list?limit=1000');
            if (response.data.success) {
                setVendors(response.data.data.vendors || []);
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
            alert('Failed to load vendors');
        }
    };

    const fetchAddons = async (vendorId) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/vendor-addon/addons/${vendorId}?includeInactive=true`);
            if (response.data.success) {
                setAddons(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching add-ons:', error);
            alert('Failed to load add-ons');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (addon = null) => {
        if (addon) {
            setEditingAddon(addon);
            setFormData({
                name: addon.name,
                description: addon.description || '',
                category: addon.category,
                price: addon.price,
                image: addon.image || '',
                eligibleForSubsidy: addon.eligibleForSubsidy,
                vendorId: addon.vendorId
            });
            setImagePreview(addon.image || '');
        } else {
            setEditingAddon(null);
            setFormData({
                name: '',
                description: '',
                category: 'other',
                price: '',
                image: '',
                eligibleForSubsidy: true,
                vendorId: selectedVendor
            });
            setImagePreview('');
        }
        setImageFile(null);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingAddon(null);
        setFormData({
            name: '',
            description: '',
            category: 'other',
            price: '',
            image: '',
            eligibleForSubsidy: true,
            vendorId: ''
        });
        setImageFile(null);
        setImagePreview('');
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert('Image size should be less than 5MB');
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.price) {
            alert('Name and price are required');
            return;
        }

        if (!selectedVendor && !formData.vendorId) {
            alert('Please select a vendor');
            return;
        }

        try {
            setUploadingImage(true);

            // Create FormData and append all fields
            const formDataToSubmit = new FormData();
            formDataToSubmit.append('name', formData.name);
            formDataToSubmit.append('description', formData.description || '');
            formDataToSubmit.append('category', formData.category);
            formDataToSubmit.append('price', parseFloat(formData.price));
            formDataToSubmit.append('eligibleForSubsidy', formData.eligibleForSubsidy);
            formDataToSubmit.append('vendorId', formData.vendorId || selectedVendor);
            
            // Append image file if selected
            if (imageFile) {
                formDataToSubmit.append('image', imageFile);
            }

            if (editingAddon) {
                await axiosInstance.put(`/vendor-addon/addons/${editingAddon._id}`, formDataToSubmit, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                alert('Add-on updated successfully');
            } else {
                await axiosInstance.post('/vendor-addon/addons', formDataToSubmit, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                alert('Add-on created successfully');
            }

            handleCloseModal();
            if (selectedVendor) {
                fetchAddons(selectedVendor);
            }
        } catch (error) {
            console.error('Error saving add-on:', error);
            alert(error.response?.data?.message || 'Failed to save add-on');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleToggleStatus = async (addonId) => {
        try {
            await axiosInstance.patch(`/vendor-addon/addons/${addonId}/toggle`);
            fetchAddons(selectedVendor);
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to update status');
        }
    };

    const handleDelete = async (addonId) => {
        if (!window.confirm('Are you sure you want to delete this add-on?')) {
            return;
        }

        try {
            await axiosInstance.delete(`/vendor-addon/addons/${addonId}`);
            alert('Add-on deleted successfully');
            fetchAddons(selectedVendor);
        } catch (error) {
            console.error('Error deleting add-on:', error);
            alert('Failed to delete add-on');
        }
    };

    const filteredAddons = addons.filter(addon => {
        const matchesSearch = addon.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || addon.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Vendor Add-ons</h1>
                    <p className="text-gray-600 text-sm mt-1">Manage vendor touch-ups and add-on services</p>
                </div>
                {selectedVendor && (
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-[#B082BE] text-white rounded-lg hover:bg-[#9a6ba3] transition-colors"
                    >
                        <FiPlus size={20} />
                        Add New Add-on
                    </button>
                )}
            </div>

            {/* Vendor Selection */}
            <div className="bg-white rounded-3xl shadow-card p-4 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Vendor
                </label>
                <select
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                >
                    <option value="">-- Select a Vendor --</option>
                    {vendors.map(vendor => (
                        <option key={vendor._id} value={vendor._id}>
                            {vendor.store_name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Filters and Search */}
            {selectedVendor && (
                <div className="bg-white rounded-3xl shadow-card p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search add-ons..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                            />
                        </div>
                        <div className="relative">
                            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                            >
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Add-ons Grid */}
            {selectedVendor && (
                <div className="bg-white rounded-3xl shadow-card p-6">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B082BE] mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading add-ons...</p>
                        </div>
                    ) : filteredAddons.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600">No add-ons found</p>
                            <button
                                onClick={() => handleOpenModal()}
                                className="mt-4 text-[#B082BE] hover:underline"
                            >
                                Create your first add-on
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredAddons.map(addon => (
                                <div
                                    key={addon._id}
                                    className={`border rounded-lg overflow-hidden ${
                                        addon.isAvailable ? 'border-gray-200' : 'border-red-200 opacity-60'
                                    }`}
                                >
                                    {addon.image ? (
                                        <img
                                            src={addon.image}
                                            alt={addon.name}
                                            className="w-full h-40 object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                                            <FiImage size={48} className="text-gray-400" />
                                        </div>
                                    )}
                                    <div className="p-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-semibold text-gray-800">{addon.name}</h3>
                                            <span className="text-[#B082BE] font-bold">₹{addon.price}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-2">{addon.description}</p>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                {categories.find(c => c.value === addon.category)?.label || addon.category}
                                            </span>
                                            {addon.eligibleForSubsidy && (
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                                    Subsidy Eligible
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleToggleStatus(addon._id)}
                                                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded ${
                                                    addon.isAvailable
                                                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                                        : 'bg-red-50 text-red-600 hover:bg-red-100'
                                                }`}
                                            >
                                                {addon.isAvailable ? <FiToggleRight size={18} /> : <FiToggleLeft size={18} />}
                                                {addon.isAvailable ? 'Active' : 'Inactive'}
                                            </button>
                                            <button
                                                onClick={() => handleOpenModal(addon)}
                                                className="px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                            >
                                                <FiEdit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(addon._id)}
                                                className="px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
                                            >
                                                <FiTrash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">
                                {editingAddon ? 'Edit Add-on' : 'Create New Add-on'}
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={3}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Category
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Price (₹) *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.price}
                                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            min="0"
                                            step="0.01"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B082BE] focus:border-transparent"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Image
                                    </label>
                                    
                                    {/* Image Preview */}
                                    {imagePreview && (
                                        <div className="mb-3 relative inline-block">
                                            <img 
                                                src={imagePreview} 
                                                alt="Preview" 
                                                className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setImageFile(null);
                                                    setImagePreview('');
                                                    setFormData({ ...formData, image: '' });
                                                }}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* File Input */}
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 px-4 py-2 bg-[#B082BE] text-white rounded-lg cursor-pointer hover:bg-[#9a6ba3] transition-colors">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <span>{imageFile ? 'Change Image' : 'Upload Image'}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                        </label>
                                        {imageFile && (
                                            <span className="text-sm text-green-600">
                                                ✓ {imageFile.name}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">
                                        Upload image (max 5MB). Recommended: 400x400px
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="eligibleForSubsidy"
                                        checked={formData.eligibleForSubsidy}
                                        onChange={(e) => setFormData({ ...formData, eligibleForSubsidy: e.target.checked })}
                                        className="w-4 h-4 text-[#B082BE] border-gray-300 rounded focus:ring-[#B082BE]"
                                    />
                                    <label htmlFor="eligibleForSubsidy" className="text-sm text-gray-700">
                                        Eligible for Membership Subsidy
                                    </label>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        disabled={uploadingImage}
                                        className="flex-1 bg-[#B082BE] text-white py-2 px-4 rounded-lg hover:bg-[#9a6ba3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            editingAddon ? 'Update Add-on' : 'Create Add-on'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorAddons;

